<template>
    <div class="kv-scroll-container" @mouseenter="onMouseEnter" @mouseleave="handleMouseLeave" ref="container">

        <div :class="['kv-scroll-content', contentClass]" ref="content" @scroll.passive="onScroll">
            <slot></slot>
        </div>

        <div class="kv-scrollbar-track" :class="{ 'kv-visible': (isHovering && !isChildHovering) || isDragging }" v-if="showScrollbar">

            <div class="kv-scrollbar-thumb" :style="{ height: thumbHeight + 'px', transform: `translateY(${thumbTop}px)` }" @mousedown.stop.prevent="startDrag">
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "KvScrollbar",
    provide() {
        return { parentScrollbar: this };
    },
    inject: {
        parentScrollbar: { default: null }
    },
    props: {
        contentClass: { type: String, default: '' }
    },
    data() {
        return {
            isHovering: false,
            isChildHovering: false,
            isDragging: false,
            showScrollbar: false, // Essa é a variável chave agora
            thumbHeight: 0,
            thumbTop: 0,
            startY: 0,
            startTop: 0,
            resizeObserver: null,
            mutationObserver: null
        };
    },
    mounted() {
        this.initObservers();
        this.calculateDimensions();
        window.addEventListener('resize', this.calculateDimensions);
    },
    updated() {
        this.$nextTick(this.calculateDimensions);
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.mutationObserver) this.mutationObserver.disconnect();
        window.removeEventListener('resize', this.calculateDimensions);
        this.removeDragEvents();
    },
    methods: {
        scrollToElementId(id, offset = 0) {
            const content = this.$refs.content;
            const target = document.getElementById(id);

            if (!content || !target) return;

            const targetRect = target.getBoundingClientRect();
            const contentRect = content.getBoundingClientRect();

            const relativeTop = targetRect.top - contentRect.top;

            content.scrollBy({
                top: relativeTop - offset, // offset opcional para dar um respiro no topo
                behavior: 'smooth'
            });
        },
        setChildHover(isHoveringChild) {
            this.isChildHovering = isHoveringChild;
        },

        // [CORRIGIDO] Só bloqueia o pai se EU tiver scrollbar
        onMouseEnter() {
            this.isHovering = true;

            if (this.parentScrollbar) {
                // Se eu tenho scroll visível (showScrollbar é true), bloqueio o pai.
                // Se eu não tenho scroll, deixo o pai em paz.
                if (this.showScrollbar) {
                    this.parentScrollbar.setChildHover(true);
                }
            }
        },

        handleMouseLeave() {
            if (!this.isDragging) {
                this.isHovering = false;
            }
            // Ao sair, SEMPRE libera o pai, por garantia
            if (this.parentScrollbar) {
                this.parentScrollbar.setChildHover(false);
            }
        },

        calculateDimensions() {
            const content = this.$refs.content;
            if (!content) return;

            const { clientHeight, scrollHeight, scrollTop } = content;

            // Lógica padrão de cálculo
            if (scrollHeight > clientHeight) {
                this.showScrollbar = true;
                const height = (clientHeight / scrollHeight) * clientHeight;
                this.thumbHeight = Math.max(height, 30);
                this.updateThumbPosition(scrollTop, scrollHeight, clientHeight);
            } else {
                this.showScrollbar = false;
                this.thumbTop = 0;
            }

            // [NOVO] Atualização Dinâmica:
            // Se o conteúdo crescer/diminuir ENQUANTO o mouse está em cima,
            // precisamos atualizar o pai em tempo real.
            if (this.isHovering && this.parentScrollbar) {
                // Se agora tenho scrollbar, bloqueia o pai. Se perdi a scrollbar, libera o pai.
                this.parentScrollbar.setChildHover(this.showScrollbar);
            }
        },

        // ... Restante dos métodos inalterados ...
        initObservers() {
            const content = this.$refs.content;
            if (!content) return;
            this.resizeObserver = new ResizeObserver(() => { this.calculateDimensions(); });
            this.resizeObserver.observe(content);
            this.mutationObserver = new MutationObserver(() => { this.calculateDimensions(); });
            this.mutationObserver.observe(content, { childList: true, subtree: true, characterData: true });
        },
        onScroll() {
            if (this.isDragging) return;
            window.requestAnimationFrame(() => {
                const content = this.$refs.content;
                if (!content) return;
                this.updateThumbPosition(content.scrollTop, content.scrollHeight, content.clientHeight);
            });
        },
        updateThumbPosition(scrollTop, scrollHeight, clientHeight) {
            const maxScrollTop = scrollHeight - clientHeight;
            if (maxScrollTop <= 0) {
                this.thumbTop = 0;
                return;
            }
            const scrollRatio = scrollTop / maxScrollTop;
            const maxThumbTop = clientHeight - this.thumbHeight;
            this.thumbTop = scrollRatio * maxThumbTop;
        },
        scrollToTop(behavior = 'smooth') {
            const content = this.$refs.content;
            if (content) content.scrollTo({ top: 0, behavior });
        },
        scrollToBottom(behavior = 'smooth') {
            const content = this.$refs.content;
            if (content) content.scrollTo({ top: content.scrollHeight, behavior });
        },
        startDrag(e) {
            this.isDragging = true;
            this.startY = e.clientY;
            this.startTop = this.thumbTop;
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.stopDrag);
        },
        onDrag(e) {
            if (!this.isDragging) return;
            const deltaY = e.clientY - this.startY;
            const content = this.$refs.content;
            const { clientHeight, scrollHeight } = content;
            const maxThumbTop = clientHeight - this.thumbHeight;
            let newTop = this.startTop + deltaY;
            if (newTop < 0) newTop = 0;
            if (newTop > maxThumbTop) newTop = maxThumbTop;
            this.thumbTop = newTop;
            const scrollRatio = newTop / maxThumbTop;
            const maxScrollTop = scrollHeight - clientHeight;
            content.scrollTop = scrollRatio * maxScrollTop;
        },
        stopDrag() {
            this.isDragging = false;
            document.body.style.userSelect = '';
            this.removeDragEvents();
            if (!this.isHovering) this.isHovering = false;

            if (this.parentScrollbar) {
                this.parentScrollbar.setChildHover(false);
            }
        },
        removeDragEvents() {
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.stopDrag);
        }
    }
};
</script>

<style>
/* CSS Mantido idêntico */
.kv-scroll-container {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
    padding-bottom: 1px;
}

.kv-scroll-content {
    height: 100%;
    width: 100%;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-right: 10px;
    padding-left: 10px;

    @media (max-width: 768px) { padding-right: 0; padding-left: 0; }
}

.kv-scroll-content::-webkit-scrollbar {
    display: none;
}

.kv-scrollbar-track {
    position: absolute;
    top: 0;
    right: 2px;
    width: 6px;
    height: 100%;
    background: transparent;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.kv-scrollbar-track.kv-visible {
    opacity: 1;
    pointer-events: auto;
}

.kv-scrollbar-thumb {
    position: absolute;
    width: 100%;
    top: 0;
    background-color: rgba(0, 0, 0, 0.4);
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    will-change: transform;
}

.kv-scrollbar-thumb:hover,
.kv-scrollbar-track.kv-visible .kv-scrollbar-thumb:active {
    background-color: rgba(0, 0, 0, 0.6);
}
</style>